import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import { IconCalendar, IconMapPin, IconDownload } from '../../components/ui/icons';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import RequestFilters, { useRequestFilters } from '../../components/requests/RequestFilters';
import { BulkActionBar, useRowSelection } from '../../components/requests/BulkSelection';
import { selectColumn, fmtDate } from '../../components/requests/RequestActions';
import ExcelDownloadModal from '../../components/requests/ExcelDownloadModal';
import { UpdateDateModal, UpdateLocationModal } from '../../components/requests/UpdateRequestModals';
import { exportCandidateRequests } from '../../api/excelTemplates';
import { listCandidateRequests } from '../../api/candidateRequests';
import { requestsQueryKey } from '../../hooks/useCandidateRequestCount';

const TYPE = 'accommodation';
// Whole calendar days from the stay's date to today (local time).
const stayedDays = (date) => {
  const [y, m, d] = date.split('-').map(Number);
  const now = new Date();
  return Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - new Date(y, m - 1, d)) / 86400000);
};

const dash = (v) => v || <span className="text-gray-400">—</span>;

const searchFields = (row) => [
  row.candidate.full_name,
  row.candidate.reference_no,
  row.candidate.passport_no,
  row.candidate.nationality,
  row.candidate.contact_no,
  row.candidate.rms_no,
  row.candidate.jde_no,
  row.candidate.source,
  row.location?.name,
];
const requestedAt = (row) => row.requested_at;

const columnsFor = (selection, onEditDate, onEditLocation, onDownload) => [
  selectColumn(selection, (r) => r.id, () => true),
  {
    key: 'candidate',
    label: 'Candidate',
    render: (row) => (
      <div>
        <Link to={`/candidates/${row.candidate.id}`} className="font-medium text-primary hover:underline">
          {row.candidate.full_name}
        </Link>
        <div className="text-xs text-gray-400">{row.candidate.reference_no}</div>
      </div>
    ),
  },
  {
    key: 'passport_nationality',
    label: 'Passport No / Nationality',
    render: (row) => (
      <div>
        {dash(row.candidate.passport_no)}
        <div className="text-xs text-gray-500">{row.candidate.nationality || '—'}</div>
      </div>
    ),
  },
  {
    key: 'vehicle_stage',
    label: 'Type / Current Stage',
    render: (row) => (
      <div>
        <span className="capitalize">{dash(row.candidate.vehicle_type)}</span>
        <div className="text-xs text-gray-500">{row.candidate.current_step || '—'}</div>
      </div>
    ),
  },
  { key: 'rms_no', label: 'RMS No', render: (row) => dash(row.candidate.rms_no) },
  { key: 'jde_no', label: 'JDE No', render: (row) => dash(row.candidate.jde_no) },
  { key: 'stamping_date', label: 'Stamping Date', render: (row) => dash(row.candidate.stamping_date) },
  { key: 'source', label: 'Source', render: (row) => dash(row.candidate.source) },
  { key: 'location', label: 'Location', render: (row) => dash(row.location?.name) },
  {
    key: 'scheduled_date',
    label: 'Date / Stayed Days',
    render: (row) => {
      if (!row.scheduled_date) return dash(null);
      const days = stayedDays(row.scheduled_date);
      return (
        <div>
          {fmtDate(row.scheduled_date)}
          {days < 0 ? (
            <div className="text-xs text-gray-500">Starts in {-days} day{days === -1 ? '' : 's'}</div>
          ) : (
            <div className="text-xs font-medium text-gray-700">
              {days} day{days === 1 ? '' : 's'}
            </div>
          )}
        </div>
      );
    },
  },
  {
    key: 'approved',
    label: 'Approved',
    render: (row) => (
      <div className="text-xs text-gray-500">
        {row.reviewed_by}
        {row.reviewed_at && <div>{fmtDate(row.reviewed_at)}</div>}
      </div>
    ),
  },
  {
    key: 'actions',
    label: '',
    render: (row) => (
      <div className="flex items-center justify-end gap-1">
        <IconButton label="Update date" tone="primary" onClick={() => onEditDate([row])}>
          <IconCalendar className="h-4 w-4" />
        </IconButton>
        <IconButton label="Update location" tone="primary" onClick={() => onEditLocation([row])}>
          <IconMapPin className="h-4 w-4" />
        </IconButton>
        <IconButton label="Download Excel" tone="primary" onClick={() => onDownload([row.id])}>
          <IconDownload className="h-4 w-4" />
        </IconButton>
      </div>
    ),
  },
];

/** Approved accommodation requests with the candidate's step details, Excel download and date/location updates. */
export default function AccommodationRegister() {
  const queryClient = useQueryClient();
  const { filters, set, apply, reset, dirty, candidate } = useRequestFilters(searchFields, requestedAt);

  const query = useQuery({
    queryKey: [...requestsQueryKey(TYPE), 'register', candidate.params],
    queryFn: () => listCandidateRequests(TYPE, { ...candidate.params, status: 'approved' }),
  });
  const allRows = query.data || [];
  const rows = apply(allRows);
  const selection = useRowSelection(rows, (r) => r.id, () => true);

  const [dateTarget, setDateTarget] = useState(null);
  const [locationTarget, setLocationTarget] = useState(null);
  const [downloadIds, setDownloadIds] = useState(null);

  const selectedRows = rows.filter((r) => selection.selectedIds.includes(r.id));
  const refetch = () => queryClient.invalidateQueries({ queryKey: requestsQueryKey(TYPE) });
  // Filters here never include a status: this page is approved requests only.
  const isDirty = dirty && (filters.search || filters.from || filters.to || candidate.active);

  return (
    <div>
      <PageHeader title="Accommodation Register" description="Approved accommodation requests — update their date and location, or download them as Excel." />

      <RequestFilters candidate={candidate} filters={filters} set={set} reset={reset} dirty={isDirty} total={allRows.length} shown={rows.length} hideStatus />

      <BulkActionBar
        selection={selection}
        actionableIds={new Set()}
        selectAllLabel={`Select all (${selection.selectableCount})`}
        extraActions={
          <>
            <Button variant="secondary" onClick={() => setDateTarget(selectedRows)}>
              Update date ({selectedRows.length})
            </Button>
            <Button variant="secondary" onClick={() => setLocationTarget(selectedRows)}>
              Update location ({selectedRows.length})
            </Button>
            <Button variant="secondary" onClick={() => setDownloadIds(selection.selectedIds)}>
              Download Excel ({selectedRows.length})
            </Button>
          </>
        }
        onApprove={() => {}}
        onReject={() => {}}
        onDone={refetch}
      />

      {downloadIds && (
        <ExcelDownloadModal open onClose={() => setDownloadIds(null)} ids={downloadIds} onExport={(ids, templateId) => exportCandidateRequests(TYPE, ids, templateId)} />
      )}

      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title="No approved requests" message={isDirty ? 'No approved requests match the current filters.' : 'No accommodation requests have been approved yet.'} />
      ) : (
        <Table columns={columnsFor(selection, setDateTarget, setLocationTarget, setDownloadIds)} data={rows} />
      )}

      <UpdateDateModal type={TYPE} requests={dateTarget} onClose={() => setDateTarget(null)} onDone={refetch} />
      <UpdateLocationModal type={TYPE} requests={locationTarget} onClose={() => setLocationTarget(null)} onDone={refetch} />
    </div>
  );
}
